// Components/PDF/ContactListGenerator.jsx
import React from "react";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import pdfStyles from "./Styles/ContactListStyles";
import header from "../../../../../assets/images/letterheadHeader.png";
import SummaryTable from "./SummaryTable";
import dayjs from "dayjs";
import { Font } from "@react-pdf/renderer";

Font.registerHyphenationCallback((word) => [word]);

const clean = (v) => (v ?? "").toString().trim();
const safeJoin = (v) => (Array.isArray(v) ? v.join(", ") : v || "-");

const joinStyles = (v) => {
  if (Array.isArray(v)) return v.map(clean).filter(Boolean).join(" / ") || "n/a";
  const s = clean(v);
  return s ? s : "-";
};

// Replace your current getTradingName with this:
const getTradingName = (c) => {
  const isAR = c?.isAR === true;
  const hasTradingName = !!clean(c?.tradingName);

  // consider array `tradingStyles` OR legacy `tradingStyle`
  const hasTradingStyle = Array.isArray(c?.tradingStyles)
    ? c.tradingStyles.some((s) => !!clean(s))
    : !!clean(c?.tradingStyle);

  // If AR, has trading style(s), but no trading name → use "FirstName LastName"
  if (isAR && !hasTradingName && hasTradingStyle) {
    const first = clean(c?.firstName);
    const last = clean(c?.lastName);
    const full = [first, last].filter(Boolean).join(" ").trim();
    if (full) return full;
  }

  // Otherwise default to tradingName → (don’t use styles as a name) → "Unknown"
  return clean(c?.tradingName || "");
};

const sortByLastThenFirst = (a, b) => {
  const la = clean(a.lastName).toLowerCase();
  const lb = clean(b.lastName).toLowerCase();
  if (la < lb) return -1;
  if (la > lb) return 1;
  const fa = clean(a.firstName).toLowerCase();
  const fb = clean(b.firstName).toLowerCase();
  return fa.localeCompare(fb);
};

const normalizeStatus = (s) => {
  if (s == null) return "-";
  if (Array.isArray(s)) return s.filter(Boolean).join(", ");
  if (typeof s === "object")
    return s.label || s.name || s.status || s.value || "-";
  if (typeof s === "boolean") return s ? "Yes" : "No";
  return String(s).trim() || "-";
};

const formatUKPhone = (num = "") => {
  const cl = String(num).replace(/\s+/g, "");
  return cl.startsWith("44") ? "0" + cl.slice(2) : cl;
};

/* ---------------- Column widths (sum to 100%) ---------------- */
// Trading Name column REMOVED. First two columns are SURNAME + FORENAME.
// We'll "span" trading name across these two columns (8% + 8% = 16%).
const COLS = {
  SURNAME: "8%",
  FORENAME: "8%",
  STATUS: "5%",
  EMAIL: "22%",
  MOBILE: "9%",
  OFFICE: "9%",
  FCA: "5%",
  ADDRESS: "27%",
  STYLES: "12%",
};
// Precompute the combined width for the group title row:
const SPAN_SURNAME_FORENAME = "21%";

/* ---------------- Header ---------------- */
const TableHeader = ({ title, hideTradingStyleColumn = false }) => (
  <>
    <Text style={[pdfStyles.subcontent, { textDecoration: "underline" }]}>
      {title}
    </Text>
    <View style={[pdfStyles.tableHeader, { fontSize: 10 }]}>
      <Text style={{ width: COLS.SURNAME }}>SURNAME</Text>
      <Text style={{ width: COLS.FORENAME }}>FORENAME</Text>
      <Text style={{ width: COLS.STATUS }}>STATUS</Text>
      <Text style={{ width: COLS.EMAIL }}>EMAIL ADDRESS</Text>
      <Text style={{ width: COLS.MOBILE }}>MOBILE NO.</Text>
      <Text style={{ width: COLS.OFFICE }}>OFFICE NO.</Text>
      <Text style={{ width: COLS.FCA }}>FCA NO.</Text>
      <Text style={{ width: COLS.ADDRESS }}>BUSINESS ADDRESS</Text>
      {!hideTradingStyleColumn && (
        <Text style={{ width: COLS.STYLES }}>TRADING STYLES</Text>
      )}
    </View>
  </>
);

/* ---------------- Group Title Row (spans SURNAME + FORENAME) ---------------- */
const GroupTitleRow = ({ trading, hideTradingStyleColumn = false }) => (
  <View
    style={[
      pdfStyles.row,
      { fontSize: 10, borderBottom: "none", marginTop: 2 },
    ]}
  >
    <Text style={{ width: SPAN_SURNAME_FORENAME, ...pdfStyles.tradingName }}>
      {trading}
    </Text>
    <Text style={{ width: COLS.STATUS }} />
    <Text style={{ width: COLS.EMAIL }} />
    <Text style={{ width: COLS.MOBILE }} />
    <Text style={{ width: COLS.OFFICE }} />
    <Text style={{ width: COLS.FCA }} />
    <Text style={{ width: COLS.ADDRESS }} />
    {!hideTradingStyleColumn && <Text style={{ width: COLS.STYLES }} />}
  </View>
);

/* ---------------- Data Row (no trading name col) ---------------- */
const Row = ({ c, isLastInGroup, hideTradingStyleColumn = false }) => {
  const email = clean(c.email || c.contactEmail || c.id || "-");
  const mobile =
    formatUKPhone(
      c.phone || c.mobile || c.mobileNumber || c.mobileTelephone || ""
    ) || "-";
  const office = formatUKPhone(c.office || c.officeNumber || "") || "-";
  const fca = clean(c.fcaNumber || c.fca || "-");
  const status = normalizeStatus(c.status ?? c.adviserStatus);
  const businessAddress = clean(c.businessAddress || "-");
  const tradingStyles = joinStyles(c.tradingStyles ?? c.tradingStyle);

  return (
    <View
      style={[
        pdfStyles.row,
        {
          fontSize: 10,
          borderBottom: isLastInGroup ? "0.5px solid #c0c0c0" : "none",
        },
      ]}
    >
      <Text style={{ width: COLS.SURNAME, ...pdfStyles.content }}>
        {c.lastName || "-"}
      </Text>
      <Text style={{ width: COLS.FORENAME, ...pdfStyles.content }}>
        {c.preferredName || c.firstName || "-"}
      </Text>
      <Text style={{ width: COLS.STATUS, ...pdfStyles.content }}>{status}</Text>
      <Text style={{ width: COLS.EMAIL, ...pdfStyles.content }}>{email}</Text>
      <Text
        style={{ width: COLS.MOBILE, textAlign: "right", ...pdfStyles.content }}
      >
        {mobile}
      </Text>
      <Text
        style={{ width: COLS.OFFICE, textAlign: "right", ...pdfStyles.content }}
      >
        {office}
      </Text>
      <Text
        style={{ width: COLS.FCA, textAlign: "right", ...pdfStyles.content }}
      >
        {fca}
      </Text>
      <Text
        style={{ width: COLS.ADDRESS, ...pdfStyles.content, marginRight: 10 }}
      >
        {businessAddress}
      </Text>
      {!hideTradingStyleColumn && (
        <Text style={{ width: COLS.STYLES, ...pdfStyles.content }}>
          {tradingStyles}
        </Text>
      )}
    </View>
  );
};

/* ---------------- Section (ARs / Non-ARs) ---------------- */
const Section = ({ title, data, ...viewProps }) => {
  const hideTradingStyleColumn = title?.toLowerCase().includes("trading style");
  // group by trading name
  const groups = {};
  data.forEach((c) => {
    const key = getTradingName(c);
    if (!groups[key]) groups[key] = [];
    groups[key].push(c);
  });

  const sortedGroups = Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, people]) => [key, people.sort(sortByLastThenFirst)]);

  return (
    <View style={{ marginTop: 16 }} {...viewProps}>
      <TableHeader title={title} hideTradingStyleColumn={hideTradingStyleColumn} />
      {sortedGroups.length ? (
        sortedGroups.map(([trading, people], idx) => (
          <View key={trading + idx} wrap={false}>
            {/* Spanned trading name row (first row of the group) */}
            <GroupTitleRow trading={trading} />
            {/* Then the normal rows for the people */}
            {people.map((c, i) => (
              <Row
                key={trading + "_" + i}
                c={c}
                isLastInGroup={i === people.length - 1}
                hideTradingStyleColumn={hideTradingStyleColumn}
              />
            ))}
          </View>
        ))
      ) : (
        <Text style={{ fontSize: 10, marginTop: 4 }}>No records found.</Text>
      )}
    </View>
  );
};

/* ---------------- Main PDF ---------------- */
export const ContactListGenerator = ({
  contacts = [],
  generatedBy = "System",
  showSummary = true,
}) => {
  const ars = contacts.filter((c) => c.isAR === true);
  const nonArs = contacts.filter((c) => c.isAR !== true);

  return (
    <Document>
      <Page size="A2" orientation="landscape" style={pdfStyles.page}>
        <Image src={header} style={pdfStyles.header} />
        <Text style={[pdfStyles.heading, { textAlign: "center" }]}>
          CONTACT LIST
        </Text>
        <Text
          style={[
            pdfStyles.tradingName,
            { marginBottom: 10, fontSize: 12, textAlign: "center" },
          ]}
        >
          {dayjs(new Date().toISOString()).format("DD MMM YYYY, hh:mm:ss A")}
        </Text>

        <Section title="APPOINTED REPRESENTATIVES" data={ars} />

        <Section title="TRADING STYLES" data={nonArs} break />

        {showSummary && <SummaryTable data={contacts} />}
        {!contacts.length && <Text>No contacts found.</Text>}
      </Page>
    </Document>
  );
};
